import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, AlertCircle, Download, Loader2, Mail, Phone } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BonoPagoCompletadoPage = () => {
  const [params] = useSearchParams();
  const purchaseId = params.get('bono_id');
  const [status, setStatus] = useState('checking');
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!purchaseId) {
      setStatus('error');
      return;
    }

    let attempts = 0;
    const maxAttempts = 10;

    const check = async () => {
      try {
        const res = await axios.get(`${API}/bonos/purchase/${purchaseId}/status`);
        if (res.data.payment_status === 'paid') {
          setData(res.data);
          setStatus('paid');
          return true;
        }
      } catch (err) {
        // Polling: 404 is transient while Redsys callback resolves.
        // Surface persistent 5xx for debugging.
        if (err?.response?.status && err.response.status >= 500) {
          console.error('Bono status poll failed:', err);
        }
      }
      return false;
    };

    const poll = async () => {
      const ok = await check();
      if (ok) return;
      attempts += 1;
      if (attempts >= maxAttempts) {
        setStatus('pending');
        return;
      }
      setTimeout(poll, 2000);
    };

    poll();
  }, [purchaseId]);

  return (
    <div className="min-h-screen bg-halo-cream py-24" data-testid="bono-payment-success">
      <div className="max-w-2xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-halo-surface rounded-2xl p-10 border border-halo-taupe/10 text-center"
        >
          {status === 'checking' && (
            <>
              <Loader2 className="mx-auto text-halo-brown animate-spin mb-4" size={48} />
              <h1 className="font-heading text-2xl text-halo-charcoal mb-2">
                Comprobando tu pago...
              </h1>
              <p className="text-halo-text">Estamos validando con el banco. Esto puede tardar unos segundos.</p>
            </>
          )}

          {status === 'paid' && data && (
            <>
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 size={48} className="text-green-600" />
              </div>
              <h1 className="font-heading text-3xl text-halo-charcoal mb-3">
                ¡Bono confirmado!
              </h1>
              <p className="text-halo-text mb-6">
                Hemos enviado tu bono y el código a <strong>{data.customer_email}</strong>.
              </p>

              <div className="bg-halo-charcoal text-halo-cream rounded-xl p-6 mb-6">
                <p className="text-halo-cream/60 text-xs tracking-widest mb-2">TU CÓDIGO</p>
                <p className="font-heading text-3xl tracking-widest mb-3">{data.code}</p>
                <p className="text-halo-cream/80 text-sm">{data.bono_name}</p>
                <p className="text-halo-cream/60 text-xs mt-2">
                  {data.sessions_total} sesiones · válido hasta {data.valid_until}
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
                <Button
                  onClick={() =>
                    window.open(`${API}/bonos/purchase/${purchaseId}/voucher-pdf`, '_blank')
                  }
                  className="bg-halo-brown hover:bg-halo-charcoal text-halo-cream"
                  data-testid="download-bono-pdf"
                >
                  <Download size={18} className="mr-2" /> Descargar bono PDF
                </Button>
                <Link to="/">
                  <Button variant="outline">Volver al inicio</Button>
                </Link>
              </div>

              <div className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-5 text-left mb-4">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">📞</span>
                  <div>
                    <h3 className="font-heading text-lg text-yellow-900 mb-1">
                      Tu reserva NO está confirmada todavía
                    </h3>
                    <p className="text-sm text-yellow-800 leading-relaxed">
                      Tu bono se ha pagado correctamente, pero <strong>la reserva queda pendiente</strong>.
                      Te llamaremos en breve al teléfono que nos has facilitado para confirmar la fecha
                      y hora de tu primera sesión. Si prefieres, puedes llamarnos directamente al{' '}
                      <a href="tel:678901086" className="font-bold underline">678 901 086</a> indicando
                      tu código <strong>{data.code}</strong>.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-halo-cream rounded-xl p-5 text-left text-sm">
                <h3 className="font-medium text-halo-charcoal mb-2">¿Cómo reservar más sesiones?</h3>
                <p className="text-halo-text mb-3">
                  Para cada sesión llámanos o escríbenos indicando tu código <strong>{data.code}</strong>:
                </p>
                <div className="flex flex-col gap-2">
                  <a
                    href="tel:678901086"
                    className="flex items-center gap-2 text-halo-brown hover:text-halo-charcoal"
                  >
                    <Phone size={14} /> 678 901 086
                  </a>
                  <a
                    href="mailto:info@halocuevas.com"
                    className="flex items-center gap-2 text-halo-brown hover:text-halo-charcoal"
                  >
                    <Mail size={14} /> info@halocuevas.com
                  </a>
                </div>
              </div>
            </>
          )}

          {status === 'pending' && (
            <>
              <Loader2 className="mx-auto text-halo-brown mb-4" size={48} />
              <h1 className="font-heading text-2xl text-halo-charcoal mb-2">
                Pago en proceso
              </h1>
              <p className="text-halo-text mb-6">
                Tu pago aún se está procesando. En cuanto lo confirmemos
                te enviaremos el bono por email. Si no recibes nada en 1 hora, contáctanos.
              </p>
              <Link to="/">
                <Button variant="outline">Volver al inicio</Button>
              </Link>
            </>
          )}

          {status === 'error' && (
            <>
              <AlertCircle className="mx-auto text-red-500 mb-4" size={48} />
              <h1 className="font-heading text-2xl text-halo-charcoal mb-2">
                No hemos podido localizar tu compra
              </h1>
              <p className="text-halo-text mb-6">
                Por favor, contacta con nosotros si has hecho un pago.
              </p>
              <Link to="/contacto">
                <Button>Contactar</Button>
              </Link>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
};


export const BonoPagoErrorPage = () => {
  return (
    <div className="min-h-screen bg-halo-cream py-24" data-testid="bono-payment-error">
      <div className="max-w-2xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-halo-surface rounded-2xl p-10 border border-halo-taupe/10 text-center"
        >
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle size={48} className="text-red-500" />
          </div>
          <h1 className="font-heading text-3xl text-halo-charcoal mb-3">
            Pago no completado
          </h1>
          <p className="text-halo-text mb-6">
            El pago se ha cancelado o ha sido rechazado por el banco. No se ha realizado ningún cargo.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/bonos">
              <Button className="bg-halo-brown hover:bg-halo-charcoal text-halo-cream">
                Probar de nuevo
              </Button>
            </Link>
            <Link to="/contacto">
              <Button variant="outline">Contactar</Button>
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};
